import {
  File,
  FileArchive,
  FileCode,
  FileImage,
  FileSpreadsheet,
  FileText,
  Mail,
  Presentation,
} from 'lucide-react'

// One visual family per kind of file, shared by the Documents list and the
// document page. Class strings are literal so Tailwind keeps them; `rgb`
// feeds the collection card's pointer spotlight (--card-glow).
const KINDS = {
  pdf: {
    label: 'PDF',
    Icon: FileText,
    rgb: '244 63 94',
    tile: 'bg-rose-50 text-rose-600 ring-rose-600/15 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-400/20',
    swatch: 'bg-rose-500',
    ink: 'text-rose-500 dark:text-rose-300',
    wash: 'from-rose-50/80 dark:from-rose-500/[0.07]',
  },
  word: {
    label: 'Word',
    Icon: FileText,
    rgb: '14 165 233',
    tile: 'bg-sky-50 text-sky-600 ring-sky-600/15 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-400/20',
    swatch: 'bg-sky-500',
    ink: 'text-sky-500 dark:text-sky-300',
    wash: 'from-sky-50/80 dark:from-sky-500/[0.07]',
  },
  sheet: {
    label: 'Spreadsheet',
    Icon: FileSpreadsheet,
    rgb: '16 185 129',
    tile: 'bg-emerald-50 text-emerald-600 ring-emerald-600/15 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/20',
    swatch: 'bg-emerald-500',
    ink: 'text-emerald-500 dark:text-emerald-300',
    wash: 'from-emerald-50/80 dark:from-emerald-500/[0.07]',
  },
  slides: {
    label: 'Presentation',
    Icon: Presentation,
    rgb: '249 115 22',
    tile: 'bg-orange-50 text-orange-600 ring-orange-600/15 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-400/20',
    swatch: 'bg-orange-500',
    ink: 'text-orange-500 dark:text-orange-300',
    wash: 'from-orange-50/80 dark:from-orange-500/[0.07]',
  },
  image: {
    label: 'Image',
    Icon: FileImage,
    rgb: '139 92 246',
    tile: 'bg-violet-50 text-violet-600 ring-violet-600/15 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-400/20',
    swatch: 'bg-violet-500',
    ink: 'text-violet-500 dark:text-violet-300',
    wash: 'from-violet-50/80 dark:from-violet-500/[0.07]',
  },
  email: {
    label: 'Email',
    Icon: Mail,
    rgb: '245 158 11',
    tile: 'bg-amber-50 text-amber-600 ring-amber-600/15 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-400/20',
    swatch: 'bg-amber-500',
    ink: 'text-amber-500 dark:text-amber-300',
    wash: 'from-amber-50/80 dark:from-amber-500/[0.07]',
  },
  code: {
    label: 'Data',
    Icon: FileCode,
    rgb: '99 102 241',
    tile: 'bg-indigo-50 text-indigo-600 ring-indigo-600/15 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-400/20',
    swatch: 'bg-indigo-500',
    ink: 'text-indigo-500 dark:text-indigo-300',
    wash: 'from-indigo-50/80 dark:from-indigo-500/[0.07]',
  },
  archive: {
    label: 'Archive',
    Icon: FileArchive,
    rgb: '100 116 139',
    tile: 'bg-gray-100 text-gray-600 ring-gray-500/15 dark:bg-gray-400/10 dark:text-gray-300 dark:ring-gray-400/20',
    swatch: 'bg-gray-500',
    ink: 'text-gray-500 dark:text-gray-300',
    wash: 'from-gray-100/80 dark:from-gray-400/[0.06]',
  },
  other: {
    label: 'File',
    Icon: File,
    rgb: '100 116 139',
    tile: 'bg-gray-100 text-gray-600 ring-gray-500/15 dark:bg-gray-400/10 dark:text-gray-300 dark:ring-gray-400/20',
    swatch: 'bg-gray-500',
    ink: 'text-gray-500 dark:text-gray-300',
    wash: 'from-gray-100/80 dark:from-gray-400/[0.06]',
  },
}

const EXT_KIND = {
  pdf: 'pdf',
  doc: 'word', docx: 'word', rtf: 'word', odt: 'word',
  xls: 'sheet', xlsx: 'sheet', csv: 'sheet', ods: 'sheet',
  ppt: 'slides', pptx: 'slides', odp: 'slides',
  png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', bmp: 'image', webp: 'image', svg: 'image', tif: 'image', tiff: 'image', heic: 'image',
  eml: 'email', msg: 'email',
  json: 'code', xml: 'code', html: 'code', htm: 'code', txt: 'code', md: 'code', log: 'code',
  zip: 'archive', rar: 'archive', '7z': 'archive', gz: 'archive', tar: 'archive',
}

const EXT_LABEL = {
  pdf: 'PDF Document',
  doc: 'Word Document', docx: 'Word Document',
  xls: 'Excel Spreadsheet', xlsx: 'Excel Spreadsheet', csv: 'CSV File',
  ppt: 'PowerPoint', pptx: 'PowerPoint',
  txt: 'Text File', eml: 'Email File', msg: 'Email File',
  json: 'JSON File', xml: 'XML File',
  png: 'Image', jpg: 'Image', jpeg: 'Image', gif: 'Image', webp: 'Image', tif: 'Image', tiff: 'Image',
}

export function fileExtension(filename) {
  if (!filename || !filename.includes('.')) return ''
  return filename.split('.').pop().toLowerCase()
}

function kindFromMime(mime = '') {
  if (mime.includes('pdf')) return 'pdf'
  if (mime.startsWith('image/')) return 'image'
  if (mime.includes('sheet') || mime.includes('excel') || mime.includes('csv')) return 'sheet'
  if (mime.includes('presentation') || mime.includes('powerpoint')) return 'slides'
  if (mime.includes('word') || mime.includes('document')) return 'word'
  if (mime.includes('message') || mime.includes('outlook')) return 'email'
  if (mime.startsWith('text/') || mime.includes('json') || mime.includes('xml')) return 'code'
  if (mime.includes('zip') || mime.includes('compressed')) return 'archive'
  return 'other'
}

/**
 * Everything the UI needs to present a file: its kind (for colour and icon),
 * a short badge ("PDF", "XLSX") and a readable type label.
 */
export function describeFile(filename, contentType) {
  const ext = fileExtension(filename)
  const key = EXT_KIND[ext] || kindFromMime(contentType || '')
  const kind = KINDS[key]
  return {
    ...kind,
    key,
    ext,
    badge: (ext || kind.label).slice(0, 4).toUpperCase(),
    label: EXT_LABEL[ext] || (ext ? `${ext.toUpperCase()} File` : kind.label),
  }
}

export function isPdfFile(filename, contentType) {
  return Boolean(contentType?.includes('pdf')) || fileExtension(filename) === 'pdf'
}

export function formatFileSize(bytes) {
  if (bytes == null || isNaN(bytes)) return '—'
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)))
  const value = bytes / Math.pow(k, i)
  return `${i === 0 ? value : parseFloat(value.toFixed(value < 10 ? 1 : 0))} ${sizes[i]}`
}

// Document lifecycle (status_types for documents: uploaded → processing →
// processed | failed). `live` statuses get a pulsing dot.
const DOCUMENT_STATUS = {
  uploaded: { label: 'Uploaded', chip: 'bg-gray-100 text-gray-600 ring-gray-500/20 dark:bg-gray-400/10 dark:text-gray-300 dark:ring-gray-400/20', dot: 'bg-gray-400' },
  pending: { label: 'Pending', chip: 'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/20', dot: 'bg-amber-500', live: true },
  processing: { label: 'Processing', chip: 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-400/10 dark:text-sky-300 dark:ring-sky-400/20', dot: 'bg-sky-500', live: true },
  processed: { label: 'Processed', chip: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/20', dot: 'bg-emerald-500' },
  analyzed: { label: 'Analyzed', chip: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/20', dot: 'bg-emerald-500' },
  failed: { label: 'Failed', chip: 'bg-rose-50 text-rose-700 ring-rose-600/20 dark:bg-rose-400/10 dark:text-rose-300 dark:ring-rose-400/20', dot: 'bg-rose-500' },
}

export function documentStatus(status, displayName) {
  const config = DOCUMENT_STATUS[status]
  if (config) return config
  return {
    ...DOCUMENT_STATUS.uploaded,
    label: displayName || (status ? status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Unknown'),
  }
}
